import {
  ArrowLeft,
  CalendarDays,
  ChevronDown,
  CircleUserRound,
  Clock3,
  Copy,
  Download,
  ExternalLink,
  FileDown,
  Link2,
  LoaderCircle,
  Mail,
  Paperclip,
  Pencil,
  Phone,
  ShieldAlert,
  SlidersHorizontal,
  Tag,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { Avatar } from "../../components/Avatar";
import { Composer } from "../../components/Composer";
import { TicketAgentNote } from "../../components/TicketAgentNote";
import { MessageAttachments } from "../../components/MessageAttachments";
import { MessageBody } from "../../components/MessageBody";
import { MessageCc } from "../../components/MessageCc";
import { PageError, PageLoader } from "../../components/StateViews";
import { QuarantineReleaseButton } from "../../components/QuarantineReleaseButton";
import { PriorityBadge, StatusBadge } from "../../components/StatusBadge";
import { useSession } from "../../context/SessionContext";
import { useToast } from "../../context/ToastContext";
import { useAsync } from "../../hooks/useAsync";
import { createSubmissionSignature, useSubmissionAttempt } from "../../hooks/useSubmissionAttempt";
import { useTicketRealtime } from "../../hooks/useTicketRealtime";
import { useStaffMessagingPreferences } from "../../hooks/useStaffMessagingPreferences";
import { customerTicketPath } from "../../lib/ticketLinks";
import { canExportTicketConversation } from "../../lib/ticketExport";
import { apiRequest, uploadAttachments } from "../../lib/api";
import { mergeLatestTicket, updateTicketDelivery } from "../../lib/mergeTicketMessages";
import { formatBytes, formatDate, formatRelative, ticketLabel } from "../../lib/format";
import type { MessageVisibility, Ticket, TicketPriority, TicketStatus } from "../../lib/types";

interface AgentDirectory {
  teams: Array<{ id: string; code: string; name: string }>;
  staff: Array<{ id: string; name: string; email: string }>;
}

function unwrapTicket(value: Ticket | { ticket: Ticket }) {
  return "ticket" in value ? value.ticket : value;
}

function contextLabel(key: string) {
  return key.replace(/[_-]+/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/^./, (letter) => letter.toUpperCase());
}

function contextValue(value: unknown) {
  if (value === null) return "None";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    const serialized = JSON.stringify(value);
    return serialized.length > 500 ? `${serialized.slice(0, 497)}...` : serialized;
  } catch {
    return "[unavailable]";
  }
}

export function AgentTicketDetailPage() {
  const { id = "" } = useParams();
  const { user, config } = useSession();
  const messagingPreferences = useStaffMessagingPreferences();
  const { showToast } = useToast();
  const [sending, setSending] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [retryingMessageId, setRetryingMessageId] = useState<string | null>(null);
  const [editingSubject, setEditingSubject] = useState(false);
  const [subjectDraft, setSubjectDraft] = useState("");
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState<"agent" | "customer" | null>(null);
  const messageInFlightRef = useRef(false);
  const spamActionInFlightRef = useRef(false);
  const refreshScopeRef = useRef({ id, sequence: 0 });
  if (refreshScopeRef.current.id !== id) refreshScopeRef.current = { id, sequence: 0 };
  const conversationScrollRef = useRef<HTMLDivElement>(null);
  const initialScrollTicketRef = useRef<string | null>(null);
  const messageSubmission = useSubmissionAttempt(`staff-message:${id}`);
  const result = useAsync(async (signal) => unwrapTicket(await apiRequest<Ticket | { ticket: Ticket }>(`/agent/tickets/${encodeURIComponent(id)}`, { signal })), [id]);
  const directory = useAsync((signal) => apiRequest<AgentDirectory>("/agent/directory", { signal }), []);

  useEffect(() => {
    setSubjectDraft(result.data?.subject ?? "");
  }, [id, result.data?.subject]);

  useEffect(() => {
    if (!result.data || initialScrollTicketRef.current === result.data.id) return;
    initialScrollTicketRef.current = result.data.id;
    window.requestAnimationFrame(() => {
      const scroller = conversationScrollRef.current;
      if (scroller) scroller.scrollTop = scroller.scrollHeight;
    });
  }, [result.data?.id]);

  async function refreshLatestMessages() {
    const scope = refreshScopeRef.current;
    if (scope.id !== id) return;
    const sequence = ++scope.sequence;
    const scroller = conversationScrollRef.current;
    const keepAtBottom = Boolean(scroller && scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 90);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20_000);
    try {
      const latest = unwrapTicket(await apiRequest<Ticket | { ticket: Ticket }>(`/agent/tickets/${encodeURIComponent(id)}`, { signal: controller.signal }));
      if (refreshScopeRef.current !== scope || sequence !== scope.sequence) return;
      result.setData((current) => refreshScopeRef.current === scope && sequence === scope.sequence ? mergeLatestTicket(current, latest) : current);
      if (keepAtBottom) window.requestAnimationFrame(() => {
        if (refreshScopeRef.current === scope && conversationScrollRef.current) conversationScrollRef.current.scrollTop = conversationScrollRef.current.scrollHeight;
      });
    } finally { window.clearTimeout(timeout); }
  }

  useTicketRealtime(result.loading ? "" : result.data?.id ?? "", refreshLatestMessages, (event) => {
    if (event.type !== "email.delivery_updated" || event.ticketId !== result.data?.id
      || typeof event.messageId !== "string" || (event.status !== "sent" && event.status !== "failed")) return;
    const messageId = event.messageId;
    const status = event.status;
    result.setData((current) => current?.id === event.ticketId ? updateTicketDelivery(current, messageId, status) : current);
  });

  function setMessageDelivery(messageId: string, deliveryStatus: NonNullable<Ticket["messages"]>[number]["deliveryStatus"]) {
    result.setData((current) => refreshScopeRef.current.id === id ? updateTicketDelivery(current, messageId, deliveryStatus) : current);
  }

  async function pollEmailDelivery(messageId: string) {
    let status: NonNullable<Ticket["messages"]>[number]["deliveryStatus"] = "pending";
    for (let attempt = 0; attempt < 20; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 700));
      const delivery = await apiRequest<{ status: NonNullable<Ticket["messages"]>[number]["deliveryStatus"] }>(
        `/agent/tickets/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}/delivery`,
      );
      status = delivery.status;
      setMessageDelivery(messageId, status);
      if (["sent", "delivered", "failed", "suppressed"].includes(status ?? "")) break;
    }
    return status;
  }

  async function updateTicket(patch: Record<string, unknown>) {
    if (!result.data || updating) return false;
    setUpdating(true);
    try {
      const updated = unwrapTicket(await apiRequest<Ticket | { ticket: Ticket }>(`/agent/tickets/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: result.data.version ? { "If-Match": `W/\"${result.data.version}\"` } : undefined,
        body: patch,
      }));
      result.setData((current) => ({
        ...updated,
        messages: current?.messages ?? updated.messages,
        messagePage: current?.messagePage ?? updated.messagePage,
      }));
      showToast("Ticket updated.", "success");
      return true;
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Ticket update failed.", "error");
      await result.refresh();
      return false;
    } finally {
      setUpdating(false);
    }
  }

  async function sendMessage(input: { body: string; html?: string; visibility: MessageVisibility; files: File[]; cc: string[] }) {
    if (messageInFlightRef.current) return;
    const draft = { body: input.body.trim(), html: input.html, visibility: input.visibility, cc: input.cc };
    const attemptKey = messageSubmission.keyFor(createSubmissionSignature(draft, input.files));
    messageInFlightRef.current = true;
    setSending(true);
    try {
      const attachments = await uploadAttachments(input.files, attemptKey);
      const created = await apiRequest<{ messageId: string }>(`/agent/tickets/${encodeURIComponent(id)}/messages`, {
        method: "POST",
        idempotencyKey: attemptKey,
        body: { ...draft, attachmentIds: attachments.map((attachment) => attachment.id) },
      });
      await refreshLatestMessages();
      if (input.visibility === "public" && created.messageId) {
        void pollEmailDelivery(created.messageId).then(() => refreshLatestMessages()).catch(() => undefined);
      }
      messageSubmission.reset(attemptKey);
      showToast(input.visibility === "internal" ? "Internal note added." : "Reply saved and email queued.", "success");
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Message could not be sent.", "error");
      throw requestError;
    } finally {
      messageInFlightRef.current = false;
      setSending(false);
    }
  }

  async function retryEmail(messageId: string) {
    if (retryingMessageId) return;
    setRetryingMessageId(messageId);
    try {
      await apiRequest(`/agent/tickets/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}/retry-email`, { method: "POST" });
      setMessageDelivery(messageId, "pending");
      const finalStatus = await pollEmailDelivery(messageId);
      await refreshLatestMessages();
      if (finalStatus === "sent" || finalStatus === "delivered") showToast("Email sent successfully.", "success");
      else if (finalStatus === "failed" || finalStatus === "suppressed") showToast("Email delivery failed. You can retry it again.", "error");
      else showToast("Email is still being delivered in the background.", "info");
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Email could not be queued again.", "error");
    } finally {
      setRetryingMessageId(null);
    }
  }

  async function loadOlderMessages() {
    const cursor = result.data?.messagePage?.nextCursor;
    if (!cursor || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const scroller = conversationScrollRef.current;
      const previousHeight = scroller?.scrollHeight ?? 0;
      const older = unwrapTicket(await apiRequest<Ticket | { ticket: Ticket }>(
        `/agent/tickets/${encodeURIComponent(id)}?before=${encodeURIComponent(cursor)}`,
      ));
      result.setData((current) => {
        if (!current) return older;
        const currentIds = new Set((current.messages ?? []).map((message) => message.id));
        return {
          ...current,
          messages: [...(older.messages ?? []).filter((message) => !currentIds.has(message.id)), ...(current.messages ?? [])],
          messagePage: older.messagePage,
        };
      });
      window.requestAnimationFrame(() => {
        if (scroller) scroller.scrollTop += scroller.scrollHeight - previousHeight;
      });
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Older messages could not be loaded.", "error");
    } finally {
      setLoadingOlder(false);
    }
  }

  async function saveTicketNote(input: { body: string; html: string; expectedRevision: number }) {
    const ticketId = result.data?.id;
    try {
      const response = await apiRequest<{ note: NonNullable<Ticket["agentNote"]> }>(
        `/agent/tickets/${encodeURIComponent(id)}/note`,
        { method: "PUT", body: input },
      );
      result.setData((current) => current && current.id === ticketId ? { ...current, agentNote: response.note } : current);
      showToast(input.body.trim() ? "Ticket note saved." : "Ticket note cleared.", "success");
    } catch (requestError) {
      // Refresh the saved baseline after a conflict without replacing the editor draft.
      void result.refresh();
      throw requestError;
    }
  }

  async function copyTicketLink(kind: "agent" | "customer", url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(kind);
      showToast(kind === "agent" ? "Agent link copied." : "Customer link copied.", "success");
      window.setTimeout(() => setCopiedLink((current) => current === kind ? null : current), 1800);
    } catch {
      showToast("The link could not be copied. Check your browser clipboard permission.", "error");
    }
  }

  async function removeEmailRedirect() {
    const current = result.data;
    if (!current?.emailFollowupTicketId || !window.confirm("Remove this email redirect? Old emails will return here if this ticket is open. If it remains Closed, the next email will create another follow-up. Existing messages will not move.")) return;
    setUpdating(true);
    try {
      const response = await apiRequest<{ ticket: Ticket }>(`/agent/tickets/${id}/email-redirect`, {
        method: "DELETE", body: { targetTicketId: current.emailFollowupTicketId, version: current.version },
      });
      result.setData(response.ticket);
      showToast("Email redirect removed. Existing messages were not moved.", "success");
    } catch (error) { showToast(error instanceof Error ? error.message : "Could not remove email redirect.", "error"); }
    finally { setUpdating(false); }
  }

  async function toggleTicketSpam() {
    const current = result.data;
    if (!current || updating || spamActionInFlightRef.current || current.quarantineType === "security" || !window.confirm(current.quarantineType === "spam"
      ? "Return this ticket to the normal queue?"
      : "Mark only this ticket as spam? The customer account and future tickets will not be affected.")) return;
    const scope = refreshScopeRef.current;
    spamActionInFlightRef.current = true;
    setUpdating(true);
    let saved = false;
    try {
      const response = await apiRequest<{ ticket: Ticket }>(`/agent/tickets/${id}/spam`, {
        method: "POST", body: { spam: current.quarantineType !== "spam", version: current.version },
      });
      saved = true;
      if (refreshScopeRef.current === scope) result.setData((previous) => mergeLatestTicket(previous, response.ticket));
      if (current.quarantineType === "spam" && response.ticket.quarantinedAt) {
        showToast("The earlier release was saved, but this ticket is under review again. Review its current state.", "info");
      } else showToast("Ticket classification updated. Customer account unchanged.", "success");
    } catch (error) { showToast(error instanceof Error ? error.message : "Ticket classification failed.", "error"); }
    try {
      // Reconcile an ambiguous response (commit followed by connection failure)
      // without resubmitting against a newer ticket version.
      await refreshLatestMessages();
    } catch {
      showToast(saved ? "Ticket classification saved, but the view could not refresh. Please reload." : "Could not refresh the ticket state. Please reload.", "error");
    } finally { spamActionInFlightRef.current = false; setUpdating(false); }
  }

  if (result.loading) return <PageLoader label="Opening ticket workspace…" />;
  const ticket = result.data;
  if (result.error || !ticket) return <PageError message={result.error ?? "Ticket not found."} onRetry={() => void result.refresh()} />;
  const canViewCustomerSensitive = user?.kind === "staff"
    && (user.permissions.includes("customer.view_sensitive") || user.permissions.includes("*"));
  const canAssign = user?.kind === "staff"
    && (user.permissions.includes("ticket.assign") || user.permissions.includes("*"));
  const canEditNote = user?.kind === "staff"
    && (user.permissions.includes("ticket.internal_note") || user.permissions.includes("*"));
  const contextEntries = Object.entries(ticket.context ?? {});
  const canUpdate = user?.kind === "staff"
    && (user.permissions.includes("ticket.update") || user.permissions.includes("*"));

  return (
    <section className="agent-ticket-workspace">
      <header className="workspace-header">
        <div className="workspace-header__title">
          <Link className="icon-button icon-button--compact" to="/agent/tickets" aria-label="Back to tickets"><ArrowLeft aria-hidden="true" size={18} /></Link>
          <div><span className="workspace-ticket-number">{ticketLabel(ticket.number)}</span>{editingSubject ? <form className="workspace-subject-editor" onSubmit={(event) => { event.preventDefault(); if (subjectDraft.trim().length >= 3) void updateTicket({ subject: subjectDraft.trim() }).then((saved) => { if (saved) setEditingSubject(false); }); }}><input autoFocus minLength={3} maxLength={500} value={subjectDraft} onChange={(event) => setSubjectDraft(event.target.value)} /><button className="button button--primary button--small" type="submit" disabled={updating}>Save</button><button className="button button--quiet button--small" type="button" onClick={() => { setSubjectDraft(ticket.subject); setEditingSubject(false); }}>Cancel</button></form> : <h1><span className="workspace-subject-text" title={ticket.subject}>{ticket.subject}</span>{canUpdate && <button className="subject-edit-button" type="button" aria-label="Edit ticket subject" onClick={() => setEditingSubject(true)}><Pencil aria-hidden="true" size={15} /></button>}</h1>}</div>
          <button className="icon-button icon-button--compact mobile-only" type="button" aria-label="Properties" title="Properties" aria-expanded={propertiesOpen} onClick={() => setPropertiesOpen(true)}><SlidersHorizontal aria-hidden="true" size={18} /></button>
        </div>
        <div className="workspace-header__actions">
          {canExportTicketConversation(user) && <Link className="button button--secondary button--small ticket-export-link" to={`/agent/tickets/${encodeURIComponent(id)}/export`} target="_blank" rel="noopener noreferrer"><FileDown aria-hidden="true" size={15} /> Export PDF</Link>}
          <button className="button button--secondary button--small" type="button" onClick={() => void copyTicketLink("agent", ticket.agentUrl ?? window.location.href)}><Copy aria-hidden="true" size={15} /> {copiedLink === "agent" ? "Copied" : "Copy agent link"}</button>
          <button className="button button--secondary button--small" type="button" onClick={() => void copyTicketLink("customer", ticket.customerUrl ?? `${window.location.origin}${customerTicketPath(ticket)}`)}><Copy aria-hidden="true" size={15} /> {copiedLink === "customer" ? "Copied" : "Copy customer link"}</button>
        </div>
      </header>

      <div className="workspace-grid">
        <main className="agent-conversation">
          <div className="conversation-meta-bar"><div><StatusBadge status={ticket.status} /><PriorityBadge priority={ticket.priority} />{ticket.sla && <span className={`sla-pill sla-pill--${ticket.sla.state}`}><Clock3 aria-hidden="true" size={14} /> {ticket.sla.label ?? (ticket.sla.dueAt ? `Due ${formatRelative(ticket.sla.dueAt)}` : ticket.sla.state)}</span>}</div><span>Created {formatDate(ticket.createdAt)}</span></div>
          <div className="ticket-context-stack">
            {ticket.context?.automaticEmailSuppressed === true && <div className="settings-callout settings-callout--warning ticket-quarantine-banner"><Mail aria-hidden="true" /><p><strong>Automatic emails disabled</strong>This ticket contains an automated email. Automatic replies and reminders stay disabled after release to prevent email loops. Any public reply you send is a deliberate agent action.</p></div>}
            {ticket.quarantinedAt && ticket.quarantineType && <div className={`settings-callout settings-callout--warning ticket-quarantine-banner ticket-quarantine-banner--${ticket.quarantineType}`}><ShieldAlert aria-hidden="true" /><p><strong>{ticket.quarantineType === "spam" ? "Spam ticket" : "Security quarantine"}</strong>{ticket.quarantineReason || (ticket.quarantineType === "spam" ? "This ticket remains visible to the customer but is excluded from the normal queue." : "This ticket is hidden from the customer until it is released.")}</p><QuarantineReleaseButton user={user} ticket={ticket} disabled={updating || sending} onBusyChange={setUpdating} onReleased={refreshLatestMessages} /></div>}
            <TicketAgentNote key={id} note={ticket.agentNote} canEdit={canEditNote} onSave={saveTicketNote} />
          </div>
          <div className="agent-conversation__scroll" ref={conversationScrollRef}>
            {ticket.messagePage?.hasMore && <div className="message-history-control"><button className="button button--secondary button--small" type="button" disabled={loadingOlder} onClick={() => void loadOlderMessages()}>{loadingOlder && <LoaderCircle className="spin" aria-hidden="true" size={14} />} {loadingOlder ? "Loading…" : "Load 10 older messages"}</button></div>}
            <div className="conversation conversation--agent" aria-label="Ticket conversation">
              {(ticket.messages ?? []).map((message) => (
                <article className={`message message--${message.visibility === "internal" ? "internal" : message.author.type === "customer" ? "customer" : "agent"}`} key={message.id}>
                  <Avatar name={message.author.name} url={message.author.avatarUrl} status={message.author.type === "staff" ? "online" : undefined} />
                  <div className="message__content">
                    <header><strong>{message.author.name}</strong>{message.visibility === "internal" && <span className="internal-label"><ShieldAlert aria-hidden="true" size={13} /> Internal note</span>}<span title={formatDate(message.createdAt)}>{formatRelative(message.createdAt)}</span></header>
                    <div className="message__routing"><span>{message.visibility === "internal" ? "Private staff note" : message.channel === "email" ? "Received by email" : message.channel === "api" ? "Sent through API" : message.channel === "chat" ? "Sent through chat" : "Sent through support portal"}</span>{message.author.email ? <span>{message.author.email}</span> : null}</div>
                    <MessageBody body={message.body} html={message.bodyHtml} />
                    <MessageCc emails={message.cc} />
                                        {message.attachments?.length ? <MessageAttachments attachments={message.attachments} canRetry /> : null}
                    {message.author.type === "staff" && message.visibility === "public" && message.deliveryStatus && <footer className={`message-delivery message-delivery--${message.deliveryStatus}`}><span>Email: {message.deliveryStatus === "pending" ? "queued" : message.deliveryStatus === "queued" ? "sending" : message.deliveryStatus}</span>{(["pending", "failed"] as const).includes(message.deliveryStatus as "pending" | "failed") && <button type="button" disabled={retryingMessageId !== null} onClick={() => void retryEmail(message.id)}>{retryingMessageId === message.id && <LoaderCircle className="spin" aria-hidden="true" size={12} />} {retryingMessageId === message.id ? "Retrying…" : "Retry email"}</button>}</footer>}
                  </div>
                </article>
              ))}
            </div>
            <div className="agent-composer-wrap">
              {ticket.status === "closed" && <div className="closed-notice"><ShieldAlert aria-hidden="true" /><div><strong>This ticket is closed</strong><p>Reopen it before sending another public reply.</p></div><button className="button button--secondary button--small" type="button" onClick={() => void updateTicket({ status: "open" })}>Reopen</button></div>}
              {ticket.status !== "closed" && messagingPreferences.error && <p className="form-alert" role="alert">Message sending preferences could not be loaded. <button type="button" onClick={() => void messagingPreferences.refresh()}>Retry</button></p>}
              {ticket.status !== "closed" && <Composer key={id} allowInternal allowCc richText initialCc={ticket.suggestedReplyCc ?? ticket.collaborators?.map((item) => item.email)} busy={sending} disabled={!messagingPreferences.data || !!messagingPreferences.error} sendDelaySeconds={messagingPreferences.data?.undoSendEnabled ? 10 : 0} maxAttachmentBytes={config.maxAttachmentBytes} onSend={sendMessage} />}
            </div>
          </div>
        </main>

        {propertiesOpen && <button className="workspace-sidebar-scrim mobile-only" type="button" onClick={() => setPropertiesOpen(false)} aria-label="Close properties" />}
        <aside className={`workspace-sidebar${propertiesOpen ? " is-open" : ""}`}>
          <section className="workspace-panel">
            <header><h2>Properties</h2><ChevronDown className="desktop-only" aria-hidden="true" size={17} /><button className="workspace-sidebar__close mobile-only" type="button" onClick={() => setPropertiesOpen(false)} aria-label="Close properties"><X aria-hidden="true" /></button></header>
            <div className="workspace-fields">
              {ticket.emailFollowupTicketId && <div className="email-redirect-notice"><strong>Email replies are redirected</strong><p>Old email replies go to the follow-up even if this ticket is reopened.</p><Link to={`/agent/tickets/${ticket.emailFollowupTicketId}`}>View follow-up ticket</Link>{canUpdate && <button className="button button--secondary button--small" type="button" disabled={updating} onClick={() => void removeEmailRedirect()}>Remove redirect</button>}</div>}
              {canUpdate && ticket.quarantineType !== "security" && <button className="button button--secondary button--small" type="button" disabled={updating} onClick={() => void toggleTicketSpam()}><ShieldAlert aria-hidden="true" size={16} />{ticket.quarantineType === "spam" ? "Not spam" : "Mark ticket as spam"}</button>}
              <label><span>Status</span><select value={ticket.status} disabled={updating} onChange={(event) => void updateTicket({ status: event.target.value as TicketStatus })}><option value="new">New</option><option value="open">Open</option><option value="pending">Pending</option><option value="on_hold">On hold</option><option value="solved">Solved</option><option value="closed">Closed</option></select></label>
              <label><span>Priority</span><select value={ticket.priority} disabled={updating} onChange={(event) => void updateTicket({ priority: event.target.value as TicketPriority })}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select></label>
              <label><span>Assignee</span>{canAssign ? <select value={ticket.assignee?.id ?? ""} disabled={updating || directory.loading} onChange={(event) => void updateTicket({ assigneeId: event.target.value || null })}><option value="">Unassigned</option>{directory.data?.staff.map((staff) => <option value={staff.id} key={staff.id}>{staff.name}</option>)}</select> : ticket.assignee ? <span className="property-person"><Avatar name={ticket.assignee.name} url={ticket.assignee.avatarUrl} size="small" /> {ticket.assignee.name}</span> : <strong>Unassigned</strong>}</label>
              <label><span>Team</span>{canAssign ? <select value={ticket.team?.id ?? ""} disabled={updating || directory.loading} onChange={(event) => void updateTicket({ teamId: event.target.value || null })}><option value="">No team</option>{directory.data?.teams.map((team) => <option value={team.id} key={team.id}>{team.name}</option>)}</select> : <strong>{ticket.team?.name ?? "Unassigned"}</strong>}</label>
              <div className="property-row property-row--tags"><span><Tag aria-hidden="true" size={14} /> Tags</span><div>{ticket.tags?.length ? ticket.tags.map((tag) => <i className="tag" key={tag}>{tag}</i>) : <em>No tags</em>}</div></div>
              {ticket.collaborators?.length ? <div className="property-row property-row--cc"><span>Recent Cc</span><div>{ticket.collaborators.map((item) => <span key={item.email}>{item.email}</span>)}</div></div> : null}
            </div>
          </section>
          <section className="workspace-panel">
            <header><h2>Customer</h2><ChevronDown aria-hidden="true" size={17} /></header>
            <div className="customer-panel"><Avatar name={ticket.requester.name} url={ticket.requester.avatarUrl} size="large" /><div><strong>{ticket.requester.name}</strong><span>Next-Cart customer</span></div></div>
            <dl className="contact-list"><div><dt><Mail aria-hidden="true" size={15} /> Email</dt><dd>{ticket.requester.email ? <a href={`mailto:${ticket.requester.email}`}>{ticket.requester.email}</a> : "Restricted"}</dd></div><div><dt><Phone aria-hidden="true" size={15} /> Phone</dt><dd>{canViewCustomerSensitive ? ticket.requester.phone ?? "Not provided" : "Restricted"}</dd></div><div><dt><CircleUserRound aria-hidden="true" size={15} /> Customer ID</dt><dd>{ticket.requester.id ?? "—"}</dd></div></dl>
            <Link className="button button--quiet button--full" to={`/agent/customers/${ticket.requester.id}`}>View customer profile <ExternalLink aria-hidden="true" size={15} /></Link>
          </section>
          {ticket.relatedObjects?.length ? <section className="workspace-panel"><header><h2>Related Next-Cart records</h2><Link2 aria-hidden="true" size={17} /></header><div className="context-cards">{ticket.relatedObjects.map((item) => <a key={`${item.type}:${item.id}`}><span>{item.type}</span><strong>{item.label ?? `#${item.id}`}</strong></a>)}</div></section> : null}
          {contextEntries.length ? <section className="workspace-panel"><header><h2>Internal context</h2><ShieldAlert aria-hidden="true" size={17} /></header><div className="context-cards">{contextEntries.map(([key, value]) => <a key={key}><span>{contextLabel(key)}</span><strong>{contextValue(value)}</strong></a>)}</div></section> : null}
        </aside>
      </div>
    </section>
  );
}
